/**
 * Create Disbursement Run Overlay — Admin Portal
 *
 * EFT / PayShap only. Wallet payouts: beneficiary = MMTP treasury account,
 * reference = recipient wallet mobile number.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  SA_BANK_OPTIONS,
  formatBankListForHelp,
  resolveCanonicalBankName,
} from '../../constants/saBanks';
import {
  accountNumberHint,
  validateSaBankAccountNumber,
} from '../../utils/saBankAccountValidation';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

type Rail = 'eft' | 'payshap';

interface ClientOption {
  id: number;
  company_name: string;
  client_code: string;
}

interface BeneficiaryRow {
  employeeRef?:   string;
  name:           string;
  accountNumber?: string;
  branchCode?:    string;
  bankName?:      string;
  amount:         string;
  reference?:     string;
}

const EMPTY_ROW: BeneficiaryRow = { name: '', accountNumber: '', bankName: '', amount: '' };

const SA_MSISDN = /^0[678]\d{8}$/;

function parseCSV(text: string): BeneficiaryRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');

  const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const idx = (keys: string[]) => keys.map((k) => header.indexOf(k)).find((i) => i >= 0) ?? -1;

  const nameIdx    = idx(['name', 'recipient_name', 'beneficiary_name', 'employee_name']);
  const amountIdx  = idx(['amount', 'amount_zar', 'salary', 'wage', 'net_pay', 'amount_(zar)']);
  const refIdx     = idx(['reference', 'payment_description', 'ref', 'narrative', 'payment_ref', 'payment_reference']);
  const empIdx     = idx(['your_reference', 'employee_ref', 'employee_id', 'emp_id', 'emp_ref', 'tx_ref']);
  const accIdx     = idx(['bank_account_number', 'account_number', 'account', 'acc_no', 'acc_number']);
  const branchIdx  = idx(['bank_branch_code', 'branch_code', 'branch', 'branch_no']);
  const bankIdx    = idx(['bank_name', 'bank']);

  if (nameIdx < 0 || amountIdx < 0) {
    throw new Error('CSV must have columns: recipient_name (or name), amount_zar (or amount)');
  }
  if (accIdx < 0) {
    throw new Error('CSV must have bank_account_number column');
  }

  return lines.slice(1).map((line, i) => {
    const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const amount = parseFloat(cols[amountIdx]);
    if (isNaN(amount) || amount <= 0) throw new Error(`Row ${i + 2}: invalid amount "${cols[amountIdx]}"`);
    return {
      employeeRef:   empIdx >= 0  ? cols[empIdx]    : undefined,
      name:          cols[nameIdx],
      accountNumber: cols[accIdx],
      branchCode:    branchIdx >= 0 ? cols[branchIdx] : undefined,
      bankName:      bankIdx >= 0 ? cols[bankIdx]   : undefined,
      amount:        amount.toFixed(2),
      reference:     refIdx >= 0  ? cols[refIdx]    : undefined,
    };
  });
}

function isTreasuryRow(row: BeneficiaryRow, treasuryAccount: string): boolean {
  if (!treasuryAccount || !row.accountNumber) return false;
  const norm = (s: string) => s.replace(/\s/g, '').replace(/^0+/, '') || '0';
  return norm(row.accountNumber) === norm(treasuryAccount);
}

export const CreateDisbursementRunOverlay: React.FC = () => {
  const navigate = useNavigate();
  const fileRef  = useRef<HTMLInputElement>(null);

  const [mode,       setMode]       = useState<'csv' | 'manual'>('csv');
  const [rail,       setRail]       = useState<Rail>('eft');
  const [clientId,   setClientId]   = useState('');
  const [clients,    setClients]    = useState<ClientOption[]>([]);
  const [treasuryAcct, setTreasuryAcct] = useState('272406481');

  const [rows,       setRows]       = useState<BeneficiaryRow[]>([{ ...EMPTY_ROW }]);
  const [rowErrors,  setRowErrors]  = useState<Record<number, string>>({});
  const [csvError,   setCsvError]   = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [census,     setCensus]     = useState<{ registered: number; unregistered: number; invalid: number } | null>(null);
  const [censusBusy, setCensusBusy] = useState(false);

  const validateRowAccount = (row: BeneficiaryRow): string | null => {
    if (isTreasuryRow(row, treasuryAcct)) return null;
    if (!row.accountNumber?.trim()) return 'Account number is required';
    return validateSaBankAccountNumber(row.accountNumber, row.bankName);
  };

  const setRowAccountError = (index: number, row: BeneficiaryRow) => {
    const err = validateRowAccount(row);
    setRowErrors((prev) => {
      const next = { ...prev };
      if (err) next[index] = err;
      else delete next[index];
      return next;
    });
    return err;
  };

  const totalAmount = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const treasuryRows = rows.filter((r) => isTreasuryRow(r, treasuryAcct));

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/disbursement-clients', {
          params: { limit: 200, activated_only: 'true', status: 'active' },
        });
        const list = res.data?.data?.clients || res.data?.data || [];
        setClients(Array.isArray(list) ? list : []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const handleCSVUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError(null);
    setCensus(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target?.result as string);
        const normalised = parsed.map((row, i) => {
          if (isTreasuryRow(row, treasuryAcct)) {
            return { ...row, bankName: 'Standard Bank' };
          }
          if (!row.bankName?.trim()) {
            throw new Error(`Row ${i + 2}: bank_name is required`);
          }
          const canonical = resolveCanonicalBankName(row.bankName);
          if (!canonical) {
            throw new Error(
              `Row ${i + 2}: unknown bank "${row.bankName}". Use one of: ${formatBankListForHelp()}`
            );
          }
          return { ...row, bankName: canonical };
        });
        setRows(normalised);
      } catch (err: unknown) {
        setCsvError(err instanceof Error ? err.message : 'Failed to parse CSV');
        setRows([]);
      }
    };
    reader.readAsText(file);
  }, [treasuryAcct]);

  const updateRow = (i: number, field: keyof BeneficiaryRow, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      if (field === 'accountNumber' || field === 'bankName') {
        setRowAccountError(i, next[i]);
      }
      return next;
    });
    setCensus(null);
  };

  const addRow    = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const runCensus = async () => {
    const msisdns = treasuryRows
      .map((r) => (r.reference || '').trim())
      .filter((m) => SA_MSISDN.test(m));
    if (msisdns.length === 0) {
      setError('Add treasury-account rows with wallet mobile numbers in the reference field first');
      return;
    }
    setCensusBusy(true);
    setError(null);
    try {
      const res = await API.post('/disbursements/registration-census', { msisdns });
      const d = res.data.data;
      setCensus({ registered: d.registeredCount, unregistered: d.unregisteredCount, invalid: d.invalidCount });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax?.response?.data?.error || 'Failed to run wallet check');
    } finally {
      setCensusBusy(false);
    }
  };

  const validateRows = (): string | null => {
    if (!clientId) return 'Select the channel partner (client) for this run';
    if (rows.length === 0) return 'Add at least one beneficiary';
    for (const r of rows) {
      if (!r.name || !r.accountNumber || !r.amount) {
        return 'Each row needs name, account number, and amount';
      }
      if (!isTreasuryRow(r, treasuryAcct) && !r.bankName?.trim()) {
        return `Row "${r.name}": bank is required (branch code is resolved automatically)`;
      }
      const acctErr = validateRowAccount(r);
      if (acctErr) {
        return `Row "${r.name}": ${acctErr}`;
      }
      if (isTreasuryRow(r, treasuryAcct)) {
        const mobile = (r.reference || '').trim();
        if (!SA_MSISDN.test(mobile)) {
          return `Treasury account row "${r.name}": reference must be wallet mobile (10 digits, 06/07/08)`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateRows();
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError(null);
    try {
      const res = await API.post('/disbursements', {
        clientId:      Number(clientId),
        rail,
        beneficiaries: rows,
      });

      const runId = res.data.data.run.id;
      await API.post(`/disbursements/${runId}/submit`);
      navigate(`/admin/disbursements/${runId}`, { state: { justCreated: true } });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; errors?: { msg: string }[] } } };
      setError(ax?.response?.data?.error || ax?.response?.data?.errors?.[0]?.msg || 'Failed to create disbursement run');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = (type: 'bank' | 'wallet') => {
    const csv = type === 'wallet'
      ? `your_reference,recipient_name,bank_account_number,bank_name,amount_zar,reference\nREP001,Jane Smith,${treasuryAcct},Standard Bank,500.00,0821234567`
      : 'your_reference,recipient_name,bank_account_number,bank_name,amount_zar,payment_description\nEMP001,Jane Smith,10111730633,Standard Bank,500.00,Commission June 2026';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = type === 'wallet' ? 'disbursement_wallet_via_treasury_template.csv' : 'pain001_payment_template.csv';
    a.click();
  };

  const selectedClient = clients.find((c) => String(c.id) === clientId);

  return (
    <div className="space-y-6">
      <div className="mymoolah-card p-6 flex items-center gap-4">
        <button onClick={() => navigate('/admin/disbursements')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
          ← Back
        </button>
        <div>
          <h2 className="admin-text-heading text-xl mb-0.5">New Disbursement Run</h2>
          <p className="admin-text-body text-gray-500 text-sm">Ad hoc run — upload a CSV or enter beneficiaries manually</p>
        </div>
      </div>

      <div className="mymoolah-card p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Run Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Channel Partner *</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 outline-none">
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name} ({c.client_code}) — id {c.id}
                </option>
              ))}
            </select>
            {clients.length === 0 && (
              <p className="mt-1 text-xs text-gray-400">Or enter numeric id if list unavailable:</p>
            )}
            {clients.length === 0 && (
              <input type="number" value={clientId} onChange={(e) => setClientId(e.target.value)}
                placeholder="Client id"
                className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment rail *</label>
            <select value={rail} onChange={(e) => setRail(e.target.value as Rail)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 outline-none">
              <option value="eft">EFT — next business day</option>
              <option value="payshap">PayShap — instant (higher cost)</option>
            </select>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg p-3 leading-relaxed">
          <strong>External bank:</strong> use <code className="font-mono">bank_account_number</code> +{' '}
          <code className="font-mono">bank_name</code> (universal branch code is resolved automatically).{' '}
          <code className="font-mono">payment_description</code> is optional narrative.{' '}
          <strong>MyMoolah wallet:</strong> use MMTP treasury account <code className="font-mono">{treasuryAcct}</code> and put the{' '}
          <strong>wallet mobile number in the reference field</strong> (not payment_description). Unregistered mobiles are rejected before submission.
        </p>
        <p className="mt-2 text-xs text-gray-400">
          Results notifications are configured at client onboarding (not per run).
        </p>
      </div>

      <div className="mymoolah-card p-6">
        <div className="flex gap-3 mb-5">
          {['csv', 'manual'].map((m) => (
            <button key={m} onClick={() => setMode(m as 'csv' | 'manual')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ${mode === m ? 'bg-[var(--foreground)] text-white' : 'bg-[var(--muted)] text-[var(--muted-foreground)]'}`}>
              {m === 'csv' ? 'Upload CSV' : 'Manual Entry'}
            </button>
          ))}
        </div>

        {mode === 'csv' && (
          <div>
            <div
              className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <p className="font-medium text-gray-700">Click to upload CSV</p>
              <p className="text-sm text-gray-400 mt-1">
                Columns: recipient_name, bank_account_number, bank_name, amount_zar
              </p>
              <p className="text-xs text-gray-400 mt-2 max-w-lg mx-auto">
                bank_name must be one of: {formatBankListForHelp()}
              </p>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleCSVUpload} className="hidden" />
            {csvError && <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg p-3">{csvError}</p>}
            {rows.length > 0 && !csvError && (
              <p className="mt-3 text-sm text-green-700 bg-green-50 rounded-lg p-3">
                {rows.length} beneficiaries loaded — total{' '}
                {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(totalAmount)}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-3">
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => downloadTemplate('bank')}>
                Download bank account template
              </button>
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => downloadTemplate('wallet')}>
                Download MyMoolah wallet template
              </button>
            </div>
          </div>
        )}

        {mode === 'manual' && (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mb-3">
                <thead>
                  <tr className="text-left bg-gray-50 border-b border-gray-100">
                    {['Tx Ref', 'Name *', 'Account No *', 'Bank *', 'Amount (R) *', 'Reference', ''].map((h) => (
                      <th key={h} className="px-3 py-2 text-xs font-semibold text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={row.employeeRef || ''}
                          onChange={(e) => updateRow(i, 'employeeRef', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-300 outline-none"
                          style={{ minWidth: '80px' }}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={row.name || ''}
                          onChange={(e) => updateRow(i, 'name', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-300 outline-none"
                          style={{ minWidth: '140px' }}
                        />
                      </td>
                      <td className="px-1 py-1 align-top">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={row.accountNumber || ''}
                          placeholder={
                            isTreasuryRow(row, treasuryAcct)
                              ? treasuryAcct
                              : accountNumberHint(row.bankName) || 'Account number'
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            setRows((prev) => {
                              const next = [...prev];
                              const updated = { ...next[i], accountNumber: v };
                              if (isTreasuryRow(updated, treasuryAcct)) {
                                updated.bankName = 'Standard Bank';
                              }
                              next[i] = updated;
                              const err = validateRowAccount(updated);
                              setRowErrors((pe) => {
                                const ne = { ...pe };
                                if (err) ne[i] = err;
                                else delete ne[i];
                                return ne;
                              });
                              return next;
                            });
                            setCensus(null);
                          }}
                          onBlur={() => setRowAccountError(i, rows[i])}
                          className={`w-full border rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-300 outline-none ${
                            rowErrors[i] ? 'border-red-400 bg-red-50' : 'border-gray-200'
                          }`}
                          style={{ minWidth: '100px' }}
                          aria-invalid={rowErrors[i] ? true : undefined}
                        />
                        {rowErrors[i] && (
                          <p className="mt-1 text-[10px] text-red-600 leading-tight max-w-[160px]">
                            {rowErrors[i]}
                          </p>
                        )}
                      </td>
                      <td className="px-1 py-1">
                        {isTreasuryRow(row, treasuryAcct) ? (
                          <span className="px-2 py-1.5 text-xs text-gray-500">Standard Bank</span>
                        ) : (
                          <select
                            value={row.bankName || ''}
                            onChange={(e) => updateRow(i, 'bankName', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:ring-1 focus:ring-blue-300 outline-none"
                            style={{ minWidth: '140px' }}
                            aria-label="Recipient bank"
                          >
                            <option value="">Select bank…</option>
                            {SA_BANK_OPTIONS.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          value={row.amount || ''}
                          onChange={(e) => updateRow(i, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-300 outline-none"
                          style={{ minWidth: '80px' }}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={row.reference || ''}
                          onChange={(e) => updateRow(i, 'reference', e.target.value)}
                          placeholder={
                            isTreasuryRow(row, treasuryAcct) ? '08XXXXXXXX' : 'optional'
                          }
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-300 outline-none"
                          style={{ minWidth: '100px' }}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <button type="button" onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600 px-2 text-base">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addRow} className="text-sm text-blue-600 hover:underline">+ Add row</button>
          </div>
        )}

        {treasuryRows.length > 0 && (
          <div className="mt-4">
            <button type="button" onClick={runCensus} disabled={censusBusy}
              className="text-sm px-3 py-2 rounded-xl border border-gray-200 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {censusBusy ? 'Checking…' : 'Check wallet registration (treasury rows)'}
            </button>
            {census && (
              <p className="mt-2 text-sm bg-gray-50 rounded-lg p-3">
                <strong>{census.registered}</strong> registered ·{' '}
                <strong className="text-amber-600">{census.unregistered}</strong> no wallet on file
                {census.invalid > 0 ? <> · <strong className="text-red-600">{census.invalid}</strong> invalid</> : null}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mymoolah-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">
              {rows.length} beneficiar{rows.length === 1 ? 'y' : 'ies'} ·{' '}
              <strong>Total: {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(totalAmount)}</strong>
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Client: {selectedClient ? `${selectedClient.company_name} (${selectedClient.client_code})` : clientId || '—'} ·{' '}
              Rail: {rail.toUpperCase()}
            </p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate('/admin/disbursements')}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={submitting || rows.length === 0}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[var(--foreground)] text-white disabled:opacity-50 transition-colors">
              {submitting ? 'Creating…' : 'Create & Submit for Approval'}
            </button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</p>}
        <p className="text-xs text-gray-400 mt-3">
          A second authorised user (checker) must approve this run before it is processed.
        </p>
      </div>
    </div>
  );
};
